/**
 * Public-facing helpers for CMS content (published items) and settings.
 *
 * - Uses a short in-memory TTL cache (default 30s) so multiple components can
 *   request the same endpoint without triggering duplicate network calls in a
 *   single page view, while still letting admin edits appear reasonably fast
 *   for users who already have the site open.
 * - All helpers gracefully return safe defaults on error, so the public site
 *   continues to render correctly even if the backend is unreachable.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// module-level cache: url -> { at: epoch-ms, data }
const cache = new Map();
const TTL_MS = 30 * 1000;

function absoluteUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

async function fetchWithTtl(url) {
  const now = Date.now();
  const hit = cache.get(url);
  if (hit && now - hit.at < TTL_MS) return hit.data;
  try {
    const { data } = await axios.get(url);
    cache.set(url, { at: now, data });
    return data;
  } catch {
    // On error, keep any previous stale value rather than the failed fetch
    return hit ? hit.data : null;
  }
}

/** Force refresh: drop cached entries and refetch on next call. */
export function invalidatePublicCache(prefix) {
  if (!prefix) { cache.clear(); return; }
  for (const key of cache.keys()) {
    if (key.includes(prefix)) cache.delete(key);
  }
}

/**
 * Fetch a published content collection (certificates, services).
 * Always returns an array; empty on failure or when nothing is published.
 */
export async function fetchPublicContent(collection) {
  const data = await fetchWithTtl(`${API}/content/${collection}`);
  if (!Array.isArray(data)) return [];
  return data.map((it) => ({
    ...it,
    image: absoluteUrl(it.image),
    pdf: absoluteUrl(it.pdf),
  }));
}

/**
 * Fetch a settings singleton (company_info, contact_info).
 * Always returns a plain object (possibly empty).
 */
export async function fetchPublicSettings(key) {
  const data = await fetchWithTtl(`${API}/settings/${key}`);
  const value = data && typeof data === 'object' ? data.value : null;
  return value && typeof value === 'object' ? value : {};
}

/** Helper: does a settings object contain any non-empty values? */
export function hasSettings(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => {
    if (v == null || v === '') return false;
    if (typeof v === 'object') {
      return Object.values(v).some((x) => x != null && x !== '');
    }
    return true;
  });
}

/**
 * Returns the CMS text if it looks like real content (long enough to be a
 * meaningful paragraph), else null so the caller falls back to the static
 * default. Guards against leftover test data or accidentally-truncated fields.
 *
 * Minimum length of 40 characters was chosen because every real EBC copy
 * block on the public site is well above that; anything shorter is either a
 * placeholder or accidental save.
 */
export function pickCmsText(cmsField, isAr, minChars = 40) {
  if (!cmsField || typeof cmsField !== 'object') return null;
  const val = (isAr ? cmsField.ar : cmsField.en) || '';
  const trimmed = String(val).trim();
  if (trimmed.length < minChars) return null;
  return trimmed;
}

/**
 * Simpler bilingual picker for short strings (titles, eyebrows, CTA labels).
 * Returns the trimmed value if it has any content in the current language,
 * or falls back to the sibling language, else null so the caller uses defaults.
 */
export function pickCmsShort(cmsField, isAr) {
  if (!cmsField || typeof cmsField !== 'object') return null;
  const primary   = (isAr ? cmsField.ar : cmsField.en) || '';
  const secondary = (isAr ? cmsField.en : cmsField.ar) || '';
  const value = (primary.trim() || secondary.trim());
  return value || null;
}

/**
 * Helper for the shared `section_copy` singleton. Each named slot lives under
 * `settings.section_copy.<key> = {ar, en}`. Consumers pass the current
 * language + a static fallback string; if the CMS slot is empty the fallback
 * is returned unchanged, so the design never breaks.
 */
export function makeSectionCopyPicker(sectionCopy, isAr) {
  const map = sectionCopy && typeof sectionCopy === 'object' ? sectionCopy : {};
  return (key, fallback) => {
    const picked = pickCmsShort(map[key], isAr);
    return picked || fallback;
  };
}
